import React from 'react';
import { isDefaultDatetime } from '@/util/helpers/time-helpers';
import CellChunk from '@/util/CellChunk';
import DataTableCell from '@/util/DataTableCell';
import DataTable from '@/util/DataTable';
import DataTableRow from '@/util/DataTableRow';
import { BOOL, DATETIME, FLOAT, INT } from '@/util/ColumnType';
import { identity } from 'ramda';
import { arraysEqual } from '@/util/helpers/array-helpers';
import { isLink } from '@/util/helpers/string-helpers';

export const transformCellChunksToCells = (cellChunksForRow: any) =>
  cellChunksForRow.flatMap((cellChunk: any) =>
    cellChunk._cells.map((cell: any, index: number) => ({
      columnType: cellChunk._headings[index]._columnType,
      columnName: cellChunk._headings[index]._columnName.toString(),
      rowspan: cellChunk.rowspan(),
      cell,
    })),
  );

const chunksAreEqual = (chunk1: CellChunk, chunk2: CellChunk) => {
  return arraysEqual(
    chunk1.cells(),
    chunk2.cells(),
    (cell1: DataTableCell, cell2: DataTableCell) => {
      return cell1.value() === cell2.value();
    },
  );
};

export const groupRowsIntoCellChunks = (
  dataTable: DataTable,
  expandedRows: number[],
) => {
  // split rows into cell chunks (one per alias)
  const allRows: CellChunk[][] = []; // list of CellChunk lists
  const indexesOfExpandedRows: number[] = [];
  dataTable.rows().forEach((r: DataTableRow, indexR: number) => {
    if (expandedRows.includes(r.rowId())) {
      indexesOfExpandedRows.push(indexR);
    }
    const chunksForRow = []; // list of CellChunk objects
    let cellsForChunk: any[] = []; // list of DataTableCell objects
    let headingsForChunk: any[] = []; // list of DataTableHeading objects
    let currentAlias: any = null;
    r.cells().forEach((c: DataTableCell, indexC: number) => {
      const heading = dataTable.headings()[indexC];
      const alias = heading.columnName().alias();
      if (indexC > 0 && alias !== currentAlias) {
        // chunk changed
        chunksForRow.push(
          new CellChunk(currentAlias, headingsForChunk, cellsForChunk),
        );
        headingsForChunk = [];
        cellsForChunk = [];
      }
      cellsForChunk.push(c);
      headingsForChunk.push(heading);
      currentAlias = alias;
    });
    if (cellsForChunk.length > 0) {
      chunksForRow.push(
        new CellChunk(currentAlias, headingsForChunk, cellsForChunk),
      );
    }
    allRows.push(chunksForRow);
  });

  const key = (chunk: CellChunk, colIndex: number) =>
    `${colIndex}__${chunk.alias()}`;

  // look for chunks that are repeated, and merge their rows (using rowspan)
  const lastChunkForAlias = {};
  const rowsToRender: any[][] = []; // list of CellChunk lists
  allRows.forEach((chunksForRow, rowIndex) => {
    const chunksToRender: any[] = []; // list of CellChunk objects
    const currentRowIsExpanded = indexesOfExpandedRows.indexOf(rowIndex) !== -1;
    const previousRowIsExpanded =
      indexesOfExpandedRows.indexOf(rowIndex - 1) !== -1;
    let leftChunkWasIncremented = true; // we chunk left-to-right, so only want to increment if chunk-to-our-left was also incremented
    chunksForRow.forEach((chunk, colIndex) => {
      // @ts-expect-error: TODO: infer type.
      const aboveChunk = lastChunkForAlias[key(chunk, colIndex)];
      if (
        aboveChunk &&
        leftChunkWasIncremented &&
        !currentRowIsExpanded &&
        !previousRowIsExpanded &&
        chunksAreEqual(aboveChunk, chunk)
      ) {
        // merge with above chunk
        aboveChunk.incrementRowspan();
        leftChunkWasIncremented = true;
      } else {
        // chunk is not the same as one above it
        chunksToRender.push(chunk);
        // @ts-expect-error: TODO: infer type.
        lastChunkForAlias[key(chunk, colIndex)] = chunk;
        leftChunkWasIncremented = false;
      }
    });
    rowsToRender.push(chunksToRender);
  });

  return rowsToRender;
};

const formatBoolean = (value: string) => {
  if (['t', '1'].includes(value)) return 'TRUE';
  if (['f', '0'].includes(value)) return 'FALSE';
  return value;
};

const formatLink = (value: string) => (
  <a href={value} target="_blank">
    {value}
  </a>
);

export const formatString = (value: string, maxLength = 50) => {
  if (value.length > maxLength) {
    return `${value.substring(0, maxLength)}...[TRIMMED]`;
  }
  return value;
};

const formatFloat = (value: string) => {
  try {
    return +parseFloat(value).toFixed(4);
  } catch (e) {
    window.console.error(e);
    return value;
  }
};

const formatDatetime = (value: string) => {
  // Summarize with time dimension scenario
  if (isDefaultDatetime(value)) {
    return value.slice(0, 10);
  }

  const match = value.match(/(^....-..-.. ..:..:..)(.*)/);
  if (match && match.length === 3) {
    return (
      <span>
        {match[1]} &nbsp;&nbsp;<span className="faint">{match[2]}</span>
      </span>
    );
  }
  window.console.warn(`Datetime format was not applied. Input: ${value}`);
  return value;
};

const formatEmpty = (value: any) => (value === '' ? 'EMPTY' : 'NULL');

const VALUE_TYPE_TO_FORMATTER_MAP = {
  EMPTY: formatEmpty,
  [BOOL]: formatBoolean,
  LINK: formatLink,
  [FLOAT]: formatFloat,
  [INT]: identity,
  [DATETIME]: formatDatetime,
} as const;

export const formatCellValue = (cell: DataTableCell, columnType: any) => {
  const type: keyof typeof VALUE_TYPE_TO_FORMATTER_MAP = cell.isEmpty()
    ? 'EMPTY'
    : isLink(cell._value)
    ? 'LINK'
    : columnType;

  const format = VALUE_TYPE_TO_FORMATTER_MAP[type] ?? formatString;

  return format(cell._value);
};
